import { useEffect, useRef, useState } from "react";
import { FcFolder, FcImageFile } from "react-icons/fc";
import { IoMdAdd } from "react-icons/io";
import { useDispatch } from "react-redux";
import { initializeUpload } from "../../../../store/slices/fileUpload.slice";
import uploadService from "../../../../services/fileUpload/uploadService";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

export default function FloatingActionButton({
  showUpload = false,
  showAdd = true,
  triggerAdd = () => null,
  triggerUpload = () => null,
  hidden = false,
  folderId = null, // Optional folderId for organizing uploads
}) {
  const [showButtons, setShowButtons] = useState(false);
  const fabRef = useRef();
  const fileInputRef = useRef();
  const dispatch = useDispatch();
  
  const toggleButtons = () => {
    setShowButtons((prev) => !prev);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    if (files.length > 10) {
      toast.error("You can only upload up to 10 files at once.");
      return;
    }

    try {
      files.forEach(file => {
        // Validate file object
        if (!file || !file.name || !file.size) {
          console.error('Invalid file object:', file);
          toast.error(`Invalid file: ${file?.name || 'Unknown file'}`);
          return;
        }
        
        // Generate unique file ID first
        const fileId = uuidv4();
        
        // Initialize upload in Redux with the fileId
        dispatch(initializeUpload({
          file: {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          }, // Pass only serializable file properties
          totalChunks: Math.ceil(file.size / (1024 * 1024)), // 1MB chunks
          fileId: fileId, // Pass the generated fileId
          folderId: folderId // Pass folderId if available (optional)
        }));
        
        // Debug logging
       
        
        // Store file reference in upload service using the same fileId
        uploadService.storeFile(fileId, file);
      });
      
      // Process upload queue
      uploadService.processUploadQueue();
      
      // Show success message with folder context if available
      const folderContext = folderId ? ` to folder` : '';
      toast.success(`${files.length} file(s) added to upload queue${folderContext}`);
      
    } catch (error) {
      console.error('Error initializing uploads:', error);
      toast.error('Failed to initialize uploads. Please try again.');
    }
    
    // Reset file input
    event.target.value = '';
    
    // Close the FAB
    setShowButtons(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fabRef.current && !fabRef.current.contains(event.target)) {
        setShowButtons(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (hidden) return null;

  return (
    <div ref={fabRef} className="fixed right-12 bottom-12 gap-2 ">
      {/* Show this only when button is toggled */}
      {showButtons && (
        <div className="mb-2 flex flex-col gap-2">
          {showUpload ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-14 h-14 flex items-center justify-center bg-primary-100 border rounded-full !shadow-lg  transition hover:bg-primary-200"
              title="Upload Files"
            >
              <FcImageFile className="w-5 h-5" />
            </button>
          ) : (
            ""
          )}
          {showAdd ? (
            <button
              onClick={() => triggerAdd()}
              className="w-14 h-14 flex items-center justify-center bg-primary-100 border rounded-full !shadow-lg transition"
            >
              <FcFolder className="w-5 h-5" />
            </button>
          ) : (
            ""
          )}
        </div>
      )}

      {/* Main toggle FAB */}
      <button
        onClick={toggleButtons}
        className="w-14 h-14 flex items-center justify-center bg-primary-800
        hover:bg-primary-950 text-white rounded-full transition"
      >
        <IoMdAdd
          className={`w-6 h-6 transform transition-transform duration-200 ${
            showButtons ? "rotate-45" : "rotate-0"
          }`}
        />
      </button>
      
      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        accept="*/*"
        onChange={handleFileUpload}
      />
    </div>
  );
}
